import { faExclamationTriangle, faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import React, { useState } from 'react'
import { FlexibleWidthXYPlot, LineSeries, LineSeriesPoint, MarkSeries, XAxis } from 'react-vis'
import { AnalyticsStepType, CurrencyCode } from '@citruscamps/citrus-client'
import { toCurrency } from '../../utils/currency'

const toUnit = (
  amount: number,
  unit: 'per' | 'currency' | 'percent',
  currency?: CurrencyCode,
  language?: string,
): string => {
  switch (unit) {
    case 'per':
      return `${amount}`
    case 'currency':
      return currency && language ? toCurrency({ amount, currency, language }).display : `${amount}`
    case 'percent':
      return `${amount}%`
    default:
      return ''
  }
}

const toTickLabel = (
  tick: Date,
  step: AnalyticsStepType,
  timezone: string,
  hidden?: boolean,
): string => {
  if (!tick || !!hidden) {
    return ''
  }
  switch (step) {
    case 'hourly':
      return (
        DateTime.fromJSDate(tick).setZone(timezone).toFormat('MMM d') +
        DateTime.fromJSDate(tick).setZone(timezone).toFormat(', ha').toLowerCase()
      )
    case 'daily':
      return DateTime.fromJSDate(tick).setZone(timezone).toFormat('MMM d')
    case 'weekly':
      return `${DateTime.fromJSDate(tick)
        .setZone(timezone)
        .toFormat('MMM d')} - ${DateTime.fromJSDate(tick)
        .setZone(timezone)
        .plus({ days: 7 })
        .minus({ days: 1 })
        .endOf('day')
        .toFormat('MMM d')}`
    case 'monthly':
      return DateTime.fromJSDate(tick).setZone(timezone).toFormat('MMM yyyy')
    case 'yearly':
      return DateTime.fromJSDate(tick).setZone(timezone).toFormat('yyyy')
    default:
      return ''
  }
}

const isSameTick = (step: AnalyticsStepType, a?: Date, b?: Date): boolean => {
  if (!a || !b) {
    return false
  }
  switch (step) {
    case 'hourly':
      return DateTime.fromJSDate(a).hasSame(DateTime.fromJSDate(b), 'hour')
    case 'daily':
    case 'weekly':
      return DateTime.fromJSDate(a).hasSame(DateTime.fromJSDate(b), 'day')
    case 'monthly':
      return DateTime.fromJSDate(a).hasSame(DateTime.fromJSDate(b), 'month')
    case 'yearly':
      return DateTime.fromJSDate(a).hasSame(DateTime.fromJSDate(b), 'year')
    default:
      return false
  }
}

interface IProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  title?: string
  unit?: 'per' | 'currency' | 'percent'
  step?: AnalyticsStepType
  summaryType?: 'sum' | 'average'
  currency?: CurrencyCode
  language?: string
  data?: { x: Date; y: number }[]
  pastData?: { x: Date; y: number }[]
  loading?: boolean
  timezone?: string
}

export const TrendPlot = (props: IProps) => {
  const {
    unit = 'per',
    step = 'daily',
    title,
    summaryType = 'sum',
    currency,
    language,
    loading = false,
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...restOfProps
  } = props
  const [nearestPoint, setNearestPoint] = useState<LineSeriesPoint | undefined>()

  let total: number = 0
  let minY: number = 0
  let maxY: number = 0
  const data: LineSeriesPoint[] | undefined =
    props.data &&
    props.data.map((d: { x: Date; y: number }, i: number): LineSeriesPoint => {
      const x: number = d.x.getTime()
      total += d.y
      minY = Math.min(minY, d.y)
      maxY = Math.max(maxY, d.y)
      return {
        ...d,
        x,
      }
    })
  const index: number | undefined = data?.findIndex((d) => d.x === nearestPoint?.x)
  const minDate: Date | undefined =
    data && data.length > 0
      ? new Date(Math.min(...data.map((i) => new Date(i.x).getTime())))
      : undefined
  const maxDate: Date | undefined =
    data && data.length > 0
      ? new Date(Math.max(...data.map((i) => new Date(i.x).getTime())))
      : undefined
  let pastTotal: number = 0
  let minPastX: number | undefined
  let maxPastX: number | undefined
  const pastData: LineSeriesPoint[] | undefined = props.pastData
    ? props.pastData.map((d: { x: Date; y: number }, i: number): LineSeriesPoint => {
        const x: number = d.x.getTime()
        pastTotal += d.y
        minPastX = minPastX ? Math.min(minPastX, x) : x
        maxPastX = maxPastX ? Math.max(maxPastX, x) : x
        minY = Math.min(minY, d.y)
        maxY = Math.max(maxY, d.y)
        return {
          ...d,
          x,
        }
      })
    : undefined
  const nearestPastPoint: LineSeriesPoint | undefined =
    nearestPoint && typeof index !== 'undefined' ? pastData?.[index] : undefined
  if (summaryType === 'average' && data && data.length > 0) {
    total = total / data.length
    pastTotal = pastTotal / data.length
  }
  return (
    <div className="position-relative p-2 mb-lg-5" {...restOfProps}>
      <div className="position-absolute start-0 m-2">
        <div className="small">{title || '\u00A0'}</div>
        <div className="text-primary fw-bold">
          {typeof total === 'undefined' ||
          typeof data === 'undefined' ||
          data?.length === 0 ||
          loading
            ? '\u00A0'
            : (nearestPoint
                ? toUnit(nearestPoint.y, unit, currency, language)
                : toUnit(total, unit, currency, language)) || '\u00A0'}
        </div>{' '}
        <div className="small text-body-secondary">
          {nearestPoint ? toTickLabel(new Date(nearestPoint.x), step, timezone) : '\u00A0'}
        </div>
      </div>
      {pastData && !loading && (
        <div className="position-absolute end-0 m-2 text-end">
          <div className="small">&nbsp;</div>
          <div className="text-body-secondary">
            {typeof pastTotal === 'undefined' || typeof data === 'undefined' || data?.length === 0
              ? '\u00A0'
              : (nearestPastPoint
                  ? toUnit(nearestPastPoint.y, unit, currency, language)
                  : toUnit(pastTotal, unit, currency, language)) || '\u00A0'}
          </div>
          <div className="small text-body-secondary">
            {nearestPastPoint
              ? toTickLabel(new Date(nearestPastPoint.x), step, timezone)
              : '\u00A0'}
          </div>
        </div>
      )}
      {loading ? (
        <div
          className="text-body-secondary pb-5 text-center"
          style={{ paddingTop: '5rem', minHeight: 164 }}
        >
          <FontAwesomeIcon icon={faSpinner} className="me-2" pulse />
          Loading
        </div>
      ) : typeof data !== 'undefined' && data.length > 0 ? (
        <div style={{ paddingTop: '4rem' }} onMouseLeave={() => setNearestPoint(undefined)}>
          {pastData && (
            <FlexibleWidthXYPlot
              className="position-absolute"
              xType="time"
              height={100}
              style={{ transform: 'translate(25px)' }}
            >
              <MarkSeries
                data={[
                  { x: minPastX || new Date().getTime(), y: minY },
                  { x: maxPastX || new Date().getTime(), y: maxY || 10 },
                ]}
                style={{ display: 'none' }}
              />
              <LineSeries data={pastData} color="#adb5bd" curve="curveMonotoneX" animation />
            </FlexibleWidthXYPlot>
          )}
          <FlexibleWidthXYPlot xType="time" height={100} style={{ transform: 'translate(25px)' }}>
            <XAxis
              tickSize={0}
              tickValues={data.map((d) => d.x)}
              tickFormat={(tick: Date) => {
                const isFirstTick: boolean = isSameTick(step, tick, minDate)
                const isLastTick: boolean = isSameTick(step, tick, maxDate)
                return toTickLabel(tick, step, timezone, !isFirstTick && !isLastTick)
              }}
            />
            <MarkSeries
              data={[
                { x: (minDate || new Date()).getTime(), y: minY },
                { x: (maxDate || new Date()).getTime(), y: maxY || 10 },
              ]}
              style={{ display: 'none' }}
            />
            <LineSeries
              data={data}
              color={getComputedStyle(document.body).getPropertyValue('--bs-link-color')}
              onNearestX={(point) => {
                if (point.x !== nearestPoint?.x) {
                  setNearestPoint(point)
                }
              }}
              curve="curveMonotoneX"
              animation
            />
            <MarkSeries
              data={nearestPoint ? [{ x: nearestPoint.x, y: nearestPoint.y }] : []}
              color={getComputedStyle(document.body).getPropertyValue('--bs-link-color')}
              animation
            />
          </FlexibleWidthXYPlot>
        </div>
      ) : (
        <div
          className="text-body-secondary pb-5 text-center"
          style={{ paddingTop: '5rem', minHeight: 164 }}
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          Failed to load analytics.
        </div>
      )}
    </div>
  )
}
